/** Shared input sanitizers and format checks for template-driven forms. */

export function sanitizeDigits(value: string, maxLength?: number): string {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (maxLength != null && maxLength > 0) {
    return digits.slice(0, maxLength);
  }
  return digits;
}

/**
 * Allow an optional leading minus only when `allowNegative` is true,
 * one decimal point, and digits. Trims extra decimals beyond `maxDecimals`.
 */
export function sanitizeDecimal(
  value: string,
  options?: { allowNegative?: boolean; maxDecimals?: number },
): string {
  const allowNegative = options?.allowNegative ?? false;
  const maxDecimals = options?.maxDecimals ?? 2;
  let raw = String(value ?? '').replace(/[^\d.-]/g, '');

  if (!allowNegative) {
    raw = raw.replace(/-/g, '');
  } else {
    const negative = raw.startsWith('-');
    raw = (negative ? '-' : '') + raw.replace(/-/g, '');
  }

  const firstDot = raw.indexOf('.');
  if (firstDot >= 0) {
    const before = raw.slice(0, firstDot + 1);
    const after = raw.slice(firstDot + 1).replace(/\./g, '');
    raw = before + after.slice(0, Math.max(0, maxDecimals));
  }

  return raw;
}

/** Phone chars: digits, spaces, +, -, (, ). */
export function sanitizePhone(value: string, maxLength = 20): string {
  return String(value ?? '')
    .replace(/[^\d+\-\s()]/g, '')
    .slice(0, maxLength);
}

export function isValidEmail(value: unknown): boolean {
  const email = String(value ?? '').trim();
  if (!email) {
    return false;
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** At least 7 digits after stripping formatting. */
export function isValidPhone(value: unknown): boolean {
  const digits = sanitizeDigits(String(value ?? ''));
  return digits.length >= 7 && digits.length <= 15;
}

export function emailValidationMessage(value: unknown, required = true): string | null {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? 'Email is required.' : null;
  }
  return isValidEmail(text) ? null : 'Enter a valid email address.';
}

export function phoneValidationMessage(value: unknown, required = false): string | null {
  const text = String(value ?? '').trim();
  if (!text) {
    return required ? 'Phone number is required.' : null;
  }
  return isValidPhone(text) ? null : 'Enter a valid phone number.';
}
